// Display settings (shell-ia §9.1, DESIGN-SYSTEM §15): theme, look, the agents' faces, text size, contrast, transparency, motion and the
// scene behind the glass, stored as `pref:ui.theme`, `pref:ui.look`, `pref:ui.agentFace`, `pref:ui.display` and `pref:ui.backdrop`. They are applied to <html> through WP-THEME's setters (tokens.css
// reads the attributes; public/econic-boot.js re-applies the mirror before the next first paint). WP-SHELL.
//
// Text size uses the type tokens (html[data-text-size]), not `html{zoom}`: native ECONIC screens grow their type without losing the
// layout; desk frames receive the same settings through `HostSession.display` (WP-DESK-HOST reads `useDisplaySettings()`).
import { useEffect, type ReactNode } from "react";
import { MotionConfig } from "motion/react";
import { followSystemTheme, isFaceChoice, isLook, isSceneId, lookOverridden, rememberScene, setDocumentDisplay, setDocumentFaces, setDocumentLook, setDocumentTheme, useDocumentLook, type FaceChoice, type Look, type SceneId, type TextSize, type ThemeChoice } from "../theme";
import { usePref, usePrefs } from "./PreferencesProvider";

export type DisplayChoice = "system" | "reduce" | "allow";

export interface DisplaySettings {
  /** shell-ia's steps 1 · 1.12 · 1.25 are read as default · large · larger. */
  text: TextSize;
  contrast: "normal" | "more";
  motion: DisplayChoice;
  transparency: DisplayChoice;
}

export const TEXT_SIZES: readonly TextSize[] = ["default", "large", "larger"];
const DEFAULT_DISPLAY: DisplaySettings = { text: "default", contrast: "normal", motion: "system", transparency: "system" };

function readText(v: unknown): TextSize | null {
  if (v === "default" || v === "large" || v === "larger") return v;
  if (v === 1 || v === "1") return "default";
  if (v === 1.12 || v === "1.12") return "large";
  if (v === 1.25 || v === "1.25") return "larger";
  return null;
}
const readChoice = (v: unknown): DisplayChoice | null => (v === "system" || v === "reduce" || v === "allow" ? v : null);

export function parseDisplay(raw: unknown, persona: string | null): DisplaySettings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  // Leadership presets get the next text-size step by default (DESIGN-SYSTEM §4) only while nothing is stored: the server serves the
  // catalogue default for an unset key, so the roster seeding (pref:ui.display, text 1.12, for leadership accounts) carries that default.
  const text = readText(o.text ?? o.textSize) ?? (persona === "leadership" ? "large" : DEFAULT_DISPLAY.text);
  return {
    text,
    contrast: o.contrast === "more" ? "more" : "normal",
    motion: readChoice(o.motion) ?? DEFAULT_DISPLAY.motion,
    transparency: readChoice(o.transparency) ?? DEFAULT_DISPLAY.transparency,
  };
}

const isTheme = (v: unknown): v is ThemeChoice => v === "light" || v === "dark" || v === "system";

/** The stored form (PrefCatalog `ui.display`, WP-LAYOUT-API): text as the zoom step 1 · 1.12 · 1.25. */
const TEXT_STORED: Record<TextSize, number> = { default: 1, large: 1.12, larger: 1.25 };
function stored(d: DisplaySettings): { text: number; contrast: string; motion: string; transparency: string } {
  return { text: TEXT_STORED[d.text], contrast: d.contrast, motion: d.motion, transparency: d.transparency };
}

/** The person's display settings and their setters. */
export function useDisplaySettings(): {
  display: DisplaySettings;
  setDisplay: (patch: Partial<DisplaySettings>) => void;
  theme: ThemeChoice;
  setTheme: (t: ThemeChoice) => void;
  /** The look on screen (the saved one, the device's, or the one a `?look=` address chose for the visit). */
  look: Look;
  /** The person's saved look; null while they have not chosen one. */
  storedLook: Look | null;
  setLook: (l: Look) => void;
  /** How the agents are drawn: "auto" (the person has not chosen; the tile in Classic, the Emblem elsewhere) or a style. */
  faces: FaceChoice;
  setFaces: (f: FaceChoice) => void;
  scene: SceneId | null;
  setScene: (s: SceneId) => void;
} {
  const { values } = usePrefs();
  const persona = typeof values.persona === "string" ? values.persona : null;
  const [rawDisplay, setRawDisplay] = usePref<unknown>("ui.display", null);
  const [theme, setTheme] = usePref<ThemeChoice>("ui.theme", "system", isTheme);
  // Unset (null) until the person picks one: the look this device already shows (chosen on the sign-in page, or Signature) stays.
  const [storedLook, setLookPref] = usePref<Look | null>("ui.look", null, isLook);
  const shownLook = useDocumentLook();
  const [storedFaces, setFacesPref] = usePref<FaceChoice | null>("ui.agentFace", null, isFaceChoice);
  const [rawScene, setScene] = usePref<string | null>("ui.backdrop", null);
  const display = parseDisplay(rawDisplay, persona);
  return {
    display,
    setDisplay: (patch) => setRawDisplay(stored({ ...display, ...patch })),
    theme,
    setTheme,
    look: shownLook,
    storedLook,
    // A choice the person makes wins over a `?look=` address at once (the effect below would otherwise wait for the stored value).
    setLook: (l) => { setDocumentLook(l); setLookPref(l); },
    faces: storedFaces ?? "auto",
    setFaces: (f) => { setDocumentFaces(f); setFacesPref(f); },
    scene: isSceneId(rawScene) ? rawScene : null,
    setScene: (s) => setScene(s),
  };
}

/** Applies the settings to the document while the shell is mounted, and gives motion/react the same reduced-motion answer. */
export function DisplayEffects({ children }: { children: ReactNode }) {
  const { display, theme, storedLook, faces, scene } = useDisplaySettings();
  const { synced } = usePrefs();

  useEffect(() => {
    setDocumentTheme(theme);
    if (theme !== "system") return;
    return followSystemTheme();
  }, [theme]);

  // The saved look, unless a `?look=` address chose one for this visit (demos and screenshots).
  useEffect(() => {
    if (storedLook && !lookOverridden()) setDocumentLook(storedLook);
  }, [storedLook]);

  // The person's faces once their preferences are in (until then the device's last choice, set by the boot script, stays).
  useEffect(() => {
    if (synced || faces !== "auto") setDocumentFaces(faces);
  }, [faces, synced]);

  useEffect(() => {
    setDocumentDisplay({
      textSize: display.text,
      contrast: display.contrast === "more" ? "more" : "default",
      transparency: display.transparency === "reduce" ? "reduce" : "default",
      motion: display.motion === "reduce" ? "reduce" : "default",
    });
  }, [display.text, display.contrast, display.transparency, display.motion]);

  useEffect(() => {
    rememberScene(scene);
  }, [scene]);

  // "allow" lets motion/react animate even where the device asks for less; "system" follows the device.
  const reduced = display.motion === "reduce" ? "always" : display.motion === "allow" ? "never" : "user";
  return <MotionConfig reducedMotion={reduced}>{children}</MotionConfig>;
}
